import { EFileType } from "../interfaces/file-type.enum";

/**
 * This object defines common file types and their associated file extensions.
 */
export const FILE_TYPES: { [key: string]: string[] } = {
  image: ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "avif", "svg", "webp"],
  document: ["pdf", "doc", "docx", "ppt", "pptx", "txt", "rtf", "odt"],
  spreadsheet: ["numbers", "ods", "xls", "xlsx", "csv"],
  audio: ["mp3", "wav", "ogg", "aac", "wma"],
  compressed: ["zip", "rar", "tar", "gz"],
  video: ["mp4", "mov", "avi", "mkv", "wmv", "flv", "webm", "m4v"],
};

/**
 * This array contains file extensions that are considered dangerous,
 * either because they can execute code or because they are often used in phishing attacks.
 */
export const DANGEROUS_EXTENSIONS = ["js", "php", "exe", "dmg", "sh", "pkg", "vps", "cgi", "bat", "jsp", "aspx", "so", "pyc", "pyo", "cmd"];

/**
 * This array contains file names that are considered dangerous,
 * either because they can provide unauthorized access or because they contain sensitive information.
 */
export const DANGEROUS_NAMES = [".htaccess", ".env", ".gitignore", ".eslintrc", ".eslintignore"];

/**
 * This function is used to determine the file type based on its file extension.
 * It compares the file extension with the known file extensions for different file types and returns the corresponding file type.
 * If the file extension is not recognized, it returns 'unknown'
 * @returns {string} - The type of the file. It can be 'image', 'video', 'document', 'audio', 'spreadsheet', 'compressed' or 'unknown'.
 */
export const getFileType = (extension: string): EFileType => {
  for (const type in FILE_TYPES) {
    if (FILE_TYPES[type].includes(extension.toLowerCase())) {
      return type as EFileType;
    }
  }
  return EFileType.UNKNOWN;
};

/**
 * A function to check if a file is dangerous based on its name.
 * @returns {boolean} - Returns true if the file is considered dangerous, false otherwise.
 */
export const isDangerousFile = (name: string, extension: string): boolean => {
  return DANGEROUS_EXTENSIONS.includes(extension) || DANGEROUS_NAMES.includes(name);
};
